import { create } from 'zustand';
import type { FeedItem, FeedMeta } from '@/api/types';
import { api } from '@/api/endpoints';

interface FeedState {
  items: FeedItem[];
  cursor: number;
  meta: FeedMeta | null;
  loading: boolean;
  error?: string;
  /** Карточки, по которым уже отстреливали /feed/seen в этой сессии. */
  seenIds: Set<string>;

  load: (limit?: number) => Promise<void>;
  prefetchIfNeeded: () => Promise<void>;
  next: () => FeedItem | null;
  prev: () => FeedItem | null;
  setCursor: (index: number) => void;
  current: () => FeedItem | null;
  applyDelta: (cardId: string, deltas: Partial<{ likes: number; dislikes: number; bookmarks: number; shares: number }>) => void;
  setUserAction: (cardId: string, key: 'liked' | 'disliked' | 'bookmarked', value: boolean) => void;
  setSeenProgress: (cards_seen: number, limit: number) => void;
  markSeen: (cardId: string) => void;
  hasSeen: (cardId: string) => boolean;
  reset: () => void;
}

export const useFeed = create<FeedState>((set, get) => ({
  items: [],
  cursor: 0,
  meta: null,
  loading: false,
  seenIds: new Set<string>(),

  load: async (limit = 10) => {
    set({ loading: true, error: undefined });
    try {
      const { data, meta } = await api.feed.list(limit);
      set({
        items: data,
        cursor: 0,
        meta: (meta as unknown as FeedMeta) ?? null,
        loading: false,
      });
    } catch (e) {
      set({ loading: false, error: (e as Error).message });
    }
  },

  prefetchIfNeeded: async () => {
    const { items, cursor } = get();
    // если осталось < 3 невиденных карточек — догружаем
    if (items.length - cursor > 3) return;
    try {
      const { data, meta } = await api.feed.list(10);
      set(state => {
        const seen = new Set(state.items.map(i => i.id));
        const fresh = data.filter(i => !seen.has(i.id));
        return {
          items: [...state.items, ...fresh],
          meta: (meta as unknown as FeedMeta) ?? state.meta,
        };
      });
    } catch { /* swallow, retry on next swipe */ }
  },

  next: () => {
    const { items, cursor } = get();
    if (cursor + 1 >= items.length) return null;
    set({ cursor: cursor + 1 });
    return items[cursor + 1] ?? null;
  },

  prev: () => {
    const { cursor } = get();
    if (cursor <= 0) return null;
    set({ cursor: cursor - 1 });
    return get().items[cursor - 1] ?? null;
  },

  setCursor: (index) => set({ cursor: index }),

  current: () => {
    const { items, cursor } = get();
    return items[cursor] ?? null;
  },

  applyDelta: (cardId, deltas) => {
    set(state => ({
      items: state.items.map(it => {
        if (it.type === 'ad' || it.id !== cardId) return it;
        return {
          ...it,
          stats: {
            ...it.stats,
            ...(deltas.likes != null ? { likes: it.stats.likes + deltas.likes } : {}),
            ...(deltas.dislikes != null ? { dislikes: it.stats.dislikes + deltas.dislikes } : {}),
            ...(deltas.bookmarks != null ? { bookmarks: it.stats.bookmarks + deltas.bookmarks } : {}),
            ...(deltas.shares != null ? { shares: it.stats.shares + deltas.shares } : {}),
          },
        };
      }),
    }));
  },

  setUserAction: (cardId, key, value) => {
    set(state => ({
      items: state.items.map(it => {
        if (it.type === 'ad' || it.id !== cardId) return it;
        return { ...it, user_actions: { ...it.user_actions, [key]: value } };
      }),
    }));
  },

  setSeenProgress: (cards_seen, limit) => {
    set(state => ({
      meta: {
        ...(state.meta ?? { daily_limit_reached: false, next_card_in: null }),
        daily_progress: { cards_seen, limit },
      } as FeedMeta,
    }));
  },

  markSeen: (cardId) => {
    const { seenIds } = get();
    if (seenIds.has(cardId)) return;
    const next = new Set(seenIds);
    next.add(cardId);
    set({ seenIds: next });
  },

  hasSeen: (cardId) => get().seenIds.has(cardId),

  reset: () => set({ items: [], cursor: 0, meta: null, loading: false, error: undefined, seenIds: new Set<string>() }),
}));
